import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useClientData, loadClientYearData } from './hooks/useClientData';
import DrilldownSidebar from './components/DrilldownSidebar/DrilldownSidebar';
import MapDashboard from './components/MapDashboard/MapDashboard';

import SideChartPanel from './components/SideChartPanel/SideChartPanel';
import ComparePanel from './components/ComparePanel/ComparePanel';
import ClientPicker from './components/ClientPicker/ClientPicker';
import { getAuthToken } from '../../tools/session/clientSession';
import {
  MC_COMPANY_KEY,
  setClientsConfig,
  setYearsConfig,
  getClientCities,
  findClientConfig,
  resetMysteryClientCache,
} from './services/companyFileReader';
import { fetchMysteryBootstrap, fetchMysteryComparison } from './services/APIs';
import styles from './MysteryClientDashboard.module.css';

/** Multi-city clients start on "Tutte"; single-city clients on their only city. */
const resolveDefaultCity = (company) => {
  const cities = getClientCities(company);
  return cities.length === 1 ? cities[0] : null;
};

const resolveLatestCompanyYear = (records, company, fallbackYears) => {
  const target = String(company || '').trim().toUpperCase();
  const matchingYears = records
    .filter((record) => String(record.azienda || '').trim().toUpperCase() === target)
    .map((record) => Number(record.anno))
    .filter(Number.isFinite);
  return matchingYears.length ? Math.max(...matchingYears) : fallbackYears[0] || null;
};

/**
 * MysteryClientDashboard — the full Mystery Client screen flow.
 *
 * Authentication: uses the host Traffix session; the backend decides which
 * clients the user may see. `hostCompany` is honored only for Supervisors.
 *
 * State machine:
 *  - isInitializingAuth → loading screen
 *  - finalError         → error screen
 *  - currentCompany     → main dashboard layout
 *
 * Dashboard layout:
 *  - DrilldownSidebar (left, dark)
 *  - MapDashboard     (centre, fullscreen Leaflet)
 *  - SideChartPanel   (overlays map left, when activeSub selected and !compareMode)
 *  - ComparePanel     (overlays map left, when compareMode active)
 */
const MysteryClientDashboard = ({ hostCompany = '', onLeftPanelChange }) => {
  // ── Auth state ────────────────────────────────────────────────────────────
  const [isInitializingAuth, setIsInitializingAuth] = useState(true);
  const [userCompany, setUserCompany]             = useState(null);
  const [allCompanies, setAllCompanies]           = useState([]);
  const [availableYears, setAvailableYears]       = useState([]);
  const [clients, setClients]                     = useState([]);
  const [isSupervisor, setIsSupervisor]           = useState(false);
  const [currentCompany, setCurrentCompany]       = useState(null);
  const [finalError, setFinalError]                = useState(null);

  // ── Drill-down navigation state ───────────────────────────────────────────
  const [selectedYear, setSelectedYear]           = useState(null);
  const [selectedCity, setSelectedCity]           = useState(null);
  const [activeMacro, setActiveMacro]             = useState(null);
  const [activeSub, setActiveSub]                 = useState(null);
  const [activeBreakdown, setActiveBreakdown]     = useState(null);
  const [compareMode, setCompareMode]             = useState(false);
  const [comparePanelOpen, setComparePanelOpen]   = useState(false);
  const [isSidebarOpen, setIsSidebarOpen]         = useState(false);
  const [mapRevealed, setMapRevealed]             = useState(false);

  // ── Auth effect ─────────────────────────────────────────────────────────
  useEffect(() => {
    const initializeAuthAndData = async () => {
      try {
        resetMysteryClientCache();
        if (!getAuthToken()) throw new Error('Token di accesso mancante');

        // 2. Fetch only the client configuration authorized by the backend.
        const bootstrap = await fetchMysteryBootstrap();
        const clientsConfig = bootstrap.clients || [];
        const years = bootstrap.years || [];
        setClientsConfig(clientsConfig);
        setYearsConfig(years);
        setClients(clientsConfig);
        setIsSupervisor(Boolean(bootstrap.is_supervisor));

        const sortedYears = [...years].sort((a, b) => b - a);
        setAvailableYears(sortedYears);

        // 3. Comparison records are projected and anonymized by the backend.
        const comparison = await fetchMysteryComparison();
        const comparisonRecords = (comparison.records || []).filter(Boolean);
        setAllCompanies(comparisonRecords);

        // A URL/host selection is honored only for Supervisors.
        const requestedCompany = bootstrap.is_supervisor
          ? (hostCompany.trim() && findClientConfig(hostCompany.trim())?.company)
          : null;
        const company = requestedCompany || bootstrap.active_client || null;
        if (company) localStorage.setItem(MC_COMPANY_KEY, company);
        else localStorage.removeItem(MC_COMPANY_KEY);
        setUserCompany(company);

        setSelectedYear(resolveLatestCompanyYear(comparisonRecords, company, sortedYears));

      } catch (error) {
        setFinalError(error.message || 'Impossibile caricare Mystery Client');
      } finally {
        setIsInitializingAuth(false);
      }
    };

    initializeAuthAndData();
    // hostCompany is read once at boot, exactly like the original URL parameter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data resolving ────────────────────────────────────────────────────────
  
  useEffect(() => {
    if (!userCompany || !selectedYear) {
      setCurrentCompany(null);
      return undefined;
    }
    let cancelled = false;
    const clientConfig = findClientConfig(userCompany);
    if (!clientConfig) {
      setFinalError(`Cliente non configurato: ${userCompany}`);
      return undefined;
    }
    setCurrentCompany(null);
    loadClientYearData(clientConfig, selectedYear)
      .then((record) => {
        if (!cancelled) {
          if (!record) throw new Error('Dati cliente non disponibili');
          setCurrentCompany(record);
        }
      })
      .catch((error) => {
        if (!cancelled) setFinalError(error.message || 'Dati cliente non disponibili');
      });
    return () => {
      cancelled = true;
    };
  }, [userCompany, selectedYear]);

  const { geojsonData } = useClientData(userCompany, selectedYear, selectedCity);

  // Multi-city → Tutte (null); single city → that city
  useEffect(() => {
    setSelectedCity(resolveDefaultCity(userCompany));
    setActiveMacro(null);
    setActiveSub(null);
    setActiveBreakdown(null);
    setCompareMode(false);
    setComparePanelOpen(false);
  }, [userCompany, selectedYear]);

  const resetToDefaultView = useCallback(() => {
    setSelectedCity(resolveDefaultCity(userCompany));
    setActiveMacro(null);
    setActiveSub(null);
    setActiveBreakdown(null);
    setCompareMode(false);
    setComparePanelOpen(false);
    setIsSidebarOpen(false);
  }, [userCompany]);

  // True while the drill-down sidebar or a side panel covers the map's left edge,
  // so the host can move its own overlays (e.g. its logo) out of the way.
  const showSideChartPanel = (activeMacro || activeSub) && !compareMode;
  const showComparePanel   = compareMode && comparePanelOpen;
  const leftPanelOpen = isSidebarOpen || showComparePanel || showSideChartPanel;

  useEffect(() => {
    onLeftPanelChange?.(leftPanelOpen);
  }, [leftPanelOpen, onLeftPanelChange]);

  useEffect(() => () => onLeftPanelChange?.(false), [onLeftPanelChange]);

  const isInitializing = isInitializingAuth;

  // ── Loading Progress Simulation ────────────────────────────────────────────
  const [progress, setProgress] = useState(0);
  const [showMainApp, setShowMainApp] = useState(false);

  useEffect(() => {
    if (finalError) {
      setShowMainApp(true);
      return;
    }

    let interval;
    let timer;

    if (!isInitializing) {
      // Fast-forward to 100% once loading finishes
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            timer = setTimeout(() => setShowMainApp(true), 250);
            return 100;
          }
          return Math.min(prev + 4, 100);
        });
      }, 16);
    } else {
      // Increment up to 95% while loading
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 95) return 95;
          const diff = 95 - prev;
          const step = Math.max(0.5, diff * 0.08);
          return Math.min(prev + step, 95);
        });
      }, 80);
    }

    return () => {
      clearInterval(interval);
      if (timer) clearTimeout(timer);
    };
  }, [isInitializing, finalError]);

  // ── Handlers ──────────────────────────────────────────────────────────────


  const handleMacroChange = (macroId) => {
    setActiveMacro(macroId);
    setActiveSub(null);
    setActiveBreakdown(null);
    if (compareMode) setComparePanelOpen(true);
  };

  const handleSubChange = (subKey) => {
    setActiveSub(subKey);
    setActiveBreakdown(null);
    if (compareMode) setComparePanelOpen(true);
  };

  const handleBreakdownChange = (bdKey) => {
    setActiveBreakdown(bdKey);
    if (compareMode) setComparePanelOpen(true);
  };

  const handleToggleCompare = () => {
    setCompareMode((prev) => {
      const next = !prev;
      setComparePanelOpen(next);
      return next;
    });
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    // Preserve activeMacro, activeSub, activeBreakdown and compareMode
    // so the user stays at the same navigation depth when switching cities.
  };

  const handleToggleSidebar = () => {
    if (!isSidebarOpen) {
      setIsSidebarOpen(true);
      return;
    }
    setIsSidebarOpen(false);
    // Closing the sidebar only hides it. Confronto, and the category it is
    // comparing, stay as they are.
    if (!compareMode) {
      setActiveMacro(null);
      setActiveSub(null);
      setActiveBreakdown(null);
    }
  };

  const handleClientSelect = (company) => {
    const config = findClientConfig(company);
    if (!isSupervisor || !config) return;
    localStorage.setItem(MC_COMPANY_KEY, config.company);
    setFinalError(null);
    setUserCompany(config.company);
  };

  const handleBackToClients = () => {
    if (!isSupervisor) return;
    localStorage.removeItem(MC_COMPANY_KEY);
    setCurrentCompany(null);
    setUserCompany(null);
    resetToDefaultView();
  };

  // ── Derived ───────────────────────────────────────────────────────────────


  // ── Error screen ──────────────────────────────────────────────────────────
  if (finalError) {
    return (
      <div className={styles.loadingScreen}>
        <div style={{ textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-sans)', padding: '0 2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
          <div style={{ fontSize: '1.125rem', fontWeight: 500 }}>{finalError}</div>
        </div>
      </div>
    );
  }

  // ── Loading screen ────────────────────────────────────────────────────────
  if (isInitializingAuth) {
    return <LoadingOverlay progress={progress} />;
  }

  if (isSupervisor && !userCompany) {
    return (
      <ClientPicker
        clients={clients}
        records={allCompanies}
        selectedYear={selectedYear}
        availableYears={availableYears}
        onYearChange={setSelectedYear}
        onSelect={handleClientSelect}
      />
    );
  }

  if (!currentCompany) {
    return <LoadingOverlay progress={progress} />;
  }

  // ── Main layout ───────────────────────────────────────────────────────────
  return (
    <div className={styles.appContainer}>
      {/* ── Left sidebar ── */}
      <DrilldownSidebar
        allCompanies={allCompanies}
        selectedYear={selectedYear}
        availableYears={availableYears}
        onYearChange={setSelectedYear}
        userCompany={userCompany}
        currentCompany={currentCompany}
        selectedCity={selectedCity}
        onCitySelect={handleCitySelect}
        activeMacro={activeMacro}
        activeSub={activeSub}
        activeBreakdown={activeBreakdown}
        onMacroChange={handleMacroChange}
        onSubChange={handleSubChange}
        onBreakdownChange={handleBreakdownChange}
        compareMode={compareMode}
        onToggleCompare={handleToggleCompare}
        isOpen={isSidebarOpen}
        onToggle={handleToggleSidebar}
        mapRevealed={mapRevealed}
        isSupervisor={isSupervisor}
        onBackToClients={handleBackToClients}
      />

      {/* ── Map area (takes remaining space) ── */}
      <div className={styles.mapArea}>
        {/* Side panels that overlay the map from the left */}
        <AnimatePresence>
          {showComparePanel && (
            <motion.div
              key="compare-panel"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 240 }}
              className={styles.overlayPanel}
              style={{ left: isSidebarOpen ? 'clamp(300px, 30vw, 480px)' : '0', transition: 'left 0.3s ease' }}
            >
              <ComparePanel
                company={currentCompany}
                allCompanies={allCompanies}
                activeMacro={activeMacro || 'indice_sintetico'}
                activeSub={activeSub}
                activeBreakdown={activeBreakdown}
                selectedCity={selectedCity}
                showCompanyNames={isSupervisor}
                onClose={() => setComparePanelOpen(false)}
              />
            </motion.div>
          )}

          {showSideChartPanel && (
            <motion.div
              key="chart-panel"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 240 }}
              className={styles.overlayPanel}
              style={{ left: isSidebarOpen ? 'clamp(300px, 30vw, 480px)' : '0', transition: 'left 0.3s ease' }}
            >
              <SideChartPanel
                company={currentCompany}
                macro={activeMacro}
                activeSub={activeSub}
                selectedCity={selectedCity}
                onClose={() => { setActiveSub(null); setActiveMacro(null); }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* The map itself */}
        <MapDashboard
          company={currentCompany}
          gtfsLines={geojsonData}
          activeMacro={activeMacro}
          selectedCity={selectedCity}
          userCompany={userCompany}
          onCitySelect={handleCitySelect}
          compareMode={compareMode}
          onToggleCompare={handleToggleCompare}
          isSidebarOpen={isSidebarOpen}
          onRevealChange={setMapRevealed}
        />
      </div>

      {/* Loading Screen Overlay rendered on top of everything if showMainApp is false */}
      {!showMainApp && <LoadingOverlay progress={progress} />}
    </div>
  );
};

// ── Reusable Loading Overlay Component ────────────────────────────────────────
const LoadingOverlay = ({ progress }) => {
  const roundedProgress = Math.round(progress);
  return (
    <div className={styles.loadingScreenOverlay}>
      <div className={styles.loadingBox}>
        <div className={styles.spinner}>
          <div className={styles.dot}></div>
          <div className={styles.dot}></div>
          <div className={styles.dot}></div>
          <div className={styles.dot}></div>
        </div>
        <div className={styles.loadingText}>Caricamento dati e mappa…</div>
        <div className={styles.progressContainer}>
          <div className={styles.progressTrack}>
            <div className={styles.progressBar} style={{ width: `${roundedProgress}%` }}></div>
          </div>
          <span className={styles.progressPercent}>{roundedProgress}%</span>
        </div>
      </div>
    </div>
  );
};

export default MysteryClientDashboard;
